import to from 'await-catch'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useMobileBack } from '../../context/MobileBackContext'
import { connect } from 'react-redux'
import { getClinics, getProfessionals, getSpecialties, getGenders } from '../../api'
import {
	addFlashMessage,
	hideLoader,
	showLoader,
	resetAppointmentsBookingState,
	setAppointmentsBookingMode,
	setAppointmentsBookingStep,
	setAppointmentsBookingForm,
	setAppointmentsBookingSearchFetched,
	setAppointmentsBookingSubmittedFilters,
	setAppointmentsBookingSpecialties,
	setAppointmentsBookingClinics,
	setAppointmentsBookingProfessionals,
	setAppointmentsBookingSearchResults,
	setAppointmentsBookingSelectedAppointments,
	setAppointmentsBookingSelectedTurn,
	addAppointmentsBookingCartTurn,
	clearAppointmentsBookingCart
} from '../../actions'
import { mapToOptions } from '../../utils/mapToOptions'
import { orderBy, parseProfessionalName } from '../../utils/stringsUtils'
import InitialForm from './InitialForm/InitialForm'
import SearchMap from './searchMap/searchMap'
import SelectAppointment from './selectAppointment/SelectAppointment'
import ConfirmAppointment from './confirmAppointment/ConfirmAppointment'
import MultipleAppointmentsSuccess from './multipleAppointmentsSuccess/MultipleAppointmentsSuccess'
import SignUpForm from './signUpForm/SignUpForm'
import AppointmentOptions from './AppointmentOptions'
import SearchTypeSelection from './SearchTypeSelection'
import { BOOKING_TYPES } from './constants'
import { createAppointmentBookingFormData } from '../../reducers'

const specialtiesCache = new Map()
const specialtiesInFlight = new Map()
let gendersCache = null
let gendersInFlight = null

const inFlightSpecialtiesByKey = {}

const Appointments = ({
	patient,
	booking,
	location,
	showLoader,
	hideLoader,
	addFlashMessage,
	resetAppointmentsBookingState,
	setAppointmentsBookingMode,
	setAppointmentsBookingStep,
	setAppointmentsBookingForm,
	setAppointmentsBookingSearchFetched,
	setAppointmentsBookingSubmittedFilters,
	setAppointmentsBookingSpecialties,
	setAppointmentsBookingClinics,
	setAppointmentsBookingProfessionals,
	setAppointmentsBookingSearchResults,
	setAppointmentsBookingSelectedAppointments,
	setAppointmentsBookingSelectedTurn,
	addAppointmentsBookingCartTurn,
	clearAppointmentsBookingCart
}) => {
	const activeStep = booking?.step || 0
	const formData = booking?.formData || {}
	const bookingType = booking?.mode || BOOKING_TYPES.SINGLE
	const searchResults = booking?.searchResults || []
	const specialtiesCount = booking?.catalogs?.specialties?.length || 0
	const [isEditingFromConfirm, setIsEditingFromConfirm] = useState(false)
	const [sessionAppointmentsBooked, setSessionAppointmentsBooked] = useState(0)
	const [genderOptions, setGenderOptions] = useState([])
	const [justRegistered, setJustRegistered] = useState(false)
	const { setCustomBack } = useMobileBack()
	const patientObraSocialId = patient?.idObraSocial || patient?.obraSocial?.id
	const patientPlanId = patient?.idPlan || patient?.plan?.id
	const patientExists = patient?.exists !== false || justRegistered

	const handleSelectBookingType = type => {
		setAppointmentsBookingMode(type)
		resetValues()
	}

		const resetValues = () => {
		setAppointmentsBookingForm(createAppointmentBookingFormData())
		setAppointmentsBookingSubmittedFilters(createAppointmentBookingFormData())
		setAppointmentsBookingSearchFetched(false)
		setAppointmentsBookingSearchResults([])
		setAppointmentsBookingSelectedAppointments([])
		setAppointmentsBookingSelectedTurn(null)
		setAppointmentsBookingClinics([])
		setAppointmentsBookingProfessionals({ items: [], key: null })
		clearAppointmentsBookingCart()
		setIsEditingFromConfirm(false)
	}

	const handleNext = values => {
		if (values) {
			if (values.bookingType) {
				setAppointmentsBookingMode(values.bookingType)
			}

			if (values.step) {
				setIsEditingFromConfirm(false)
				setAppointmentsBookingStep(values.step)
				return
			}

			setAppointmentsBookingForm(values)

			if (Object.prototype.hasOwnProperty.call(values, 'speciality')) {
				setAppointmentsBookingSubmittedFilters(values)
				setAppointmentsBookingSearchFetched(false)
			}
		}

		if (activeStep === 3) {
			setIsEditingFromConfirm(false)
		}

		setAppointmentsBookingStep(activeStep + 1)
	}

	const handleInitialFormNext = values => {
		if (values) {
			if (values.bookingType) {
				setAppointmentsBookingMode(values.bookingType)
			}
			
			// En turnos múltiples el form usa startDate/endDate, pero el fetch y el filtrado
			// local trabajan con dateFrom/dateTo. Propagamos la selección del usuario.
			const isMultiple = (values.bookingType || bookingType) === BOOKING_TYPES.MULTIPLE
			const mappedValues = isMultiple ? {
				...values,
				dateFrom: values.startDate || null,
				dateTo: values.endDate || null
			} : values

			setAppointmentsBookingForm(mappedValues)
			setAppointmentsBookingSubmittedFilters(mappedValues)
			setAppointmentsBookingSearchFetched(false)
			setAppointmentsBookingSearchResults([])
			setAppointmentsBookingSelectedAppointments([])
			setAppointmentsBookingSelectedTurn(null)
			clearAppointmentsBookingCart()
		}
		setAppointmentsBookingStep(activeStep + 1)
	}

	const handleUpdateFormData = values => {
		setAppointmentsBookingForm(values)
	}

	const handleBack = () => {
		setAppointmentsBookingStep(Math.max(activeStep - 1, 0))
	}

	const handleEditTurn = turn => {
		if (!turn) return

		const filteredAppointments = searchResults.filter(
			item => item.idProfesional === turn.idProfesional && item.clinica === turn.clinica
		)

		setAppointmentsBookingSelectedTurn(turn)
		setAppointmentsBookingSelectedAppointments(
			filteredAppointments.length > 0 ? filteredAppointments : [turn]
		)
		setIsEditingFromConfirm(true)
		setAppointmentsBookingStep(4)
	}

	const handleConfirmBack = () => {
		if (bookingType === BOOKING_TYPES.MULTIPLE) {
			setIsEditingFromConfirm(false)
			setAppointmentsBookingStep(3)
			return
		}

		handleBack()
	}

	const handleMultipleConfirmed = (confirmedCount = 0) => {
		setIsEditingFromConfirm(false)
		if (confirmedCount > 0) {
			setSessionAppointmentsBooked(prev => prev + confirmedCount)
		}
		setAppointmentsBookingStep(6)
	}

	const handleSingleConfirmed = () => {
		setSessionAppointmentsBooked(prev => prev + 1)
	}

	const onConfirm = (turn, options = {}) => {
		const { addToCart = bookingType === BOOKING_TYPES.MULTIPLE, continueToConfirm = false } =
			options
		const normalize = value =>
			String(value == null ? '' : value)
				.trim()
				.toLowerCase()
		const matchedClinic = (booking?.catalogs?.clinics || []).find(
			clinic => normalize(clinic.codigoEmpresa) === normalize(turn && turn.clinica)
		)
		const enrichedTurn = turn && {
			...turn,
			idClinica: turn.idClinica || (matchedClinic && matchedClinic.value) || null
		}

		setAppointmentsBookingSelectedTurn(enrichedTurn)

		if (bookingType === BOOKING_TYPES.MULTIPLE && enrichedTurn && addToCart) {
			addAppointmentsBookingCartTurn(enrichedTurn)
		}

		if (bookingType === BOOKING_TYPES.MULTIPLE && !continueToConfirm) {
			if (isEditingFromConfirm) {
				setIsEditingFromConfirm(false)
				setAppointmentsBookingStep(5)
				return
			}

			setAppointmentsBookingStep(3)
			return
		}

		if (bookingType === BOOKING_TYPES.MULTIPLE && continueToConfirm && !addToCart) {
			clearAppointmentsBookingCart()
			setIsEditingFromConfirm(false)
			setAppointmentsBookingMode(BOOKING_TYPES.SINGLE)
			setAppointmentsBookingStep(activeStep + 1)
			return
		}

		setAppointmentsBookingStep(activeStep + 1)
	}

	useEffect(() => {
		resetAppointmentsBookingState()

		return () => {
			resetAppointmentsBookingState()
		}
	}, [resetAppointmentsBookingState])

	useEffect(() => {
		if (activeStep > 0) {
			setCustomBack(() => handleBack)
		} else {
			setCustomBack(null)
		}
		return () => setCustomBack(null)
	}, [activeStep, setCustomBack])

	useEffect(() => {
		if (patientExists) return

		const fetchGenders = async () => {
			if (gendersCache) {
				setGenderOptions(gendersCache)
				return
			}
			if (!gendersInFlight) {
				gendersInFlight = to(getGenders())
			}
			const [err, response] = await gendersInFlight
			if (!err && response) {
				const data = response.data || response
				const options = mapToOptions(data, 'id', 'nombre')
				gendersCache = options
				setGenderOptions(options)
			}
		}

		fetchGenders()
	}, [patientExists])

	useEffect(() => {
		if (specialtiesCount > 0) return

		const fetchSpecialties = async () => {
			try {
				showLoader()
				const request = {
					idObraSocial: patientObraSocialId,
					idPlan: patientPlanId
				}
				const requestKey = `${patientObraSocialId}-${patientPlanId}`

				if (!inFlightSpecialtiesByKey[requestKey]) {
					inFlightSpecialtiesByKey[requestKey] = to(getSpecialties(request)).finally(() => {
						delete inFlightSpecialtiesByKey[requestKey]
					})
				}

				const [err, specialtiesResponse] = await inFlightSpecialtiesByKey[requestKey]

				if (err) throw new Error('Error cargando especialidades')

				const specialtiesData = specialtiesResponse.data || specialtiesResponse
				let optionsSpecialty = mapToOptions(specialtiesData, 'idEspecialidad', 'nombre')
				optionsSpecialty = orderBy(optionsSpecialty, 'label')
				setAppointmentsBookingSpecialties(optionsSpecialty)

			} catch (error) {
				addFlashMessage('Ocurrió un error cargando las especialidades')
				setAppointmentsBookingSpecialties([])
			} finally {
				hideLoader()
			}
		}

		fetchSpecialties()
	}, [
		patientObraSocialId,
		patientPlanId,
		specialtiesCount,
		showLoader,
		hideLoader,
		addFlashMessage,
		setAppointmentsBookingSpecialties,
	])

	const fetchClinics = async speciality => {
		try {
			if (!patientObraSocialId || !patientPlanId) {
				setAppointmentsBookingClinics([])
				return
			}

			const request = {
				IdEspecialidad: speciality,
				IdObraSocial: patientObraSocialId,
				IdPlan: patientPlanId
			}

			const [err, clinicsResponse] = await to(getClinics(request))

			if (err) {
				setAppointmentsBookingClinics([])
				return
			}

			const clinicsData = clinicsResponse.data || clinicsResponse
			const optionsClinic = mapToOptions(clinicsData, 'id', 'nombre', ['codigoEmpresa', 'direccion'])
			setAppointmentsBookingClinics(optionsClinic)
		} catch (error) {
			addFlashMessage('Ocurrió un error cargando las clínicas')
			setAppointmentsBookingClinics([])
		}
	}

	const fetchProfessionals = async (speciality, clinicCode) => {
		try {
			if (!patientObraSocialId || !patientPlanId) {
				setAppointmentsBookingProfessionals({ items: [], key: null })
				return
			}

			const clinicCodes = Array.isArray(clinicCode)
				? clinicCode.filter(Boolean)
				: clinicCode
					? [clinicCode]
					: []
			const clinicas = clinicCodes.join(',')

			const request = {
				idEspecialidad: speciality,
				idObraSocial: patientObraSocialId,
				idPlan: patientPlanId,
				esVideoconsulta: false,
				...(clinicas ? { clinicas } : {})
			}

			const [err, professionalsResponse] = await to(getProfessionals(request))

			if (err) {
				setAppointmentsBookingProfessionals({ items: [], key: null })
				return
			}

			const optionsProfessional = mapToOptions(
				professionalsResponse.data || professionalsResponse,
				'idProfesional',
				'nombreProfesional'
			).map(option => ({ ...option, label: parseProfessionalName(option.label) }))
			setAppointmentsBookingProfessionals({
				items: optionsProfessional,
				key: `${speciality}-${[...clinicCodes].sort().join(',')}`
			})
		} catch (error) {
			addFlashMessage('Ocurrió un error cargando los datos')
			setAppointmentsBookingProfessionals({ items: [], key: null })
		}
	}

	useEffect(() => {
		if (!formData.speciality) {
			setAppointmentsBookingSearchFetched(false)
			setAppointmentsBookingProfessionals({ items: [], key: null })
			setAppointmentsBookingClinics([])
			setAppointmentsBookingSearchResults([])
			setAppointmentsBookingSelectedAppointments([])
			setAppointmentsBookingSelectedTurn(null)
			clearAppointmentsBookingCart()
		} else {
			if (bookingType === BOOKING_TYPES.MULTIPLE) {
				fetchClinics()
			} else {
				fetchClinics(formData.speciality)
			}
		}
	}, [
		formData.speciality,
		setAppointmentsBookingProfessionals,
		setAppointmentsBookingClinics,
		setAppointmentsBookingSearchResults,
		setAppointmentsBookingSelectedAppointments,
		setAppointmentsBookingSelectedTurn,
		clearAppointmentsBookingCart
	])

	const renderStep = () => {
		if (!patientExists) {
			return (
				<SignUpForm
					patient={patient}
					appointment={booking}
					formData={{
						name: patient?.nombres || patient?.name || '',
						lastName: patient?.apellidos || patient?.lastName || '',
						gender: patient?.sexo || '',
						birthDate: patient?.dob || ''
					}}
					genderOptions={genderOptions}
					addFlashMessage={addFlashMessage}
					onNext={() => setJustRegistered(true)}
					showBack={false}
				/>
			)
		}

		switch (activeStep) {
			case 0:
				return <SearchTypeSelection onNext={handleNext} />
			case 1:
				return (
					<AppointmentOptions
						onNext={handleNext}
						onSelectBookingType={handleSelectBookingType}
						onBack={handleBack}
					/>
				)
			case 2:
				return (
					<InitialForm
						onNext={handleInitialFormNext}
						onBack={handleBack}
						onUpdateFormData={handleUpdateFormData}
						bookingType={bookingType}
						getProfessionals={fetchProfessionals}
					/>
				)
			case 3:
				return (
					<SearchMap
						onNext={handleNext}
						onBack={handleBack}
						bookingType={bookingType}
						getProfessionals={fetchProfessionals}
					/>
				)
			case 4:
				return <SelectAppointment onBack={handleBack} onConfirm={onConfirm} />
			case 5:
				return (
					<ConfirmAppointment
						onBack={handleConfirmBack}
						onEditTurn={handleEditTurn}
						onMultipleConfirmed={handleMultipleConfirmed}
						onConfirmed={handleSingleConfirmed}
						onBackInit={resetAppointmentsBookingState}
					/>
				)
			case 6:
				return (
					<MultipleAppointmentsSuccess
						onRetry={() => setAppointmentsBookingStep(5)}
						onBackInit={resetAppointmentsBookingState}
					/>
				)
			default:
				return <SearchTypeSelection onNext={handleNext} />
		}
	}

	return (
		<>
			{renderStep()}
		</>
	)
}

const mapDispatchToProps = {
	showLoader,
	hideLoader,
	addFlashMessage,
	resetAppointmentsBookingState,
	setAppointmentsBookingMode,
	setAppointmentsBookingStep,
	setAppointmentsBookingForm,
	setAppointmentsBookingSearchFetched,
	setAppointmentsBookingSubmittedFilters,
	setAppointmentsBookingSpecialties,
	setAppointmentsBookingClinics,
	setAppointmentsBookingProfessionals,
	setAppointmentsBookingSearchResults,
	setAppointmentsBookingSelectedAppointments,
	setAppointmentsBookingSelectedTurn,
	addAppointmentsBookingCartTurn,
	clearAppointmentsBookingCart
}

function mapStateToProps({ auth, appointment }) {
	return {
		patient: auth,
		booking: appointment.booking
	}
}

export default connect(mapStateToProps, mapDispatchToProps)(Appointments)
